import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  Stack,
  Chip,
  Tooltip,
  Toolbar,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Avatar,
  Card,
  CardContent,
  Box,
  Button,
} from '@mui/material';
import { AddRounded, ThumbUpOffAlt } from '@mui/icons-material';
import AddAnnouncement from '../Dialogs/AddAnnouncement';
import { useDispatch, useSelector } from 'react-redux';
import usePermissions from '../../Reducers/usePermissions';

import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Title as TitleIcon,
  Description as DescriptionIcon,
  CalendarToday as CalendarTodayIcon,
  LocationOn as LocationIcon,
  School as ProgramIcon,
  Person as PersonIcon,
  MoreVert as ActionsIcon
} from '@mui/icons-material';
import { DateTime } from 'luxon';
import DeleteConfirmationDialog from '../Dialogs/ConfirmDelete';
import { ADMIN, ROOT } from '../../Static/static';
import AnnouncementsAck from '../Dialogs/AnnouncementsAck';
import { useGetAnnouncements } from '../../Hooks/useGetAnnouncements';


const AnnouncementsList = () => {

  const role = useSelector((state) => state.role);
  const locations = useSelector((state) => state.role.tutor_locations);

  const [trigger, setTrigger] = useState(false);
  const [ack, setAck] = useState(false);
  const [initialData, setInitialData] = useState(null);
  const { hasPermission } = usePermissions();
  const { announcementAck ,announcements, deleteAnnouncement, fetchAnnouncements, fetchAnnouncementAck } = useGetAnnouncements();

  const [confirmDelete, setConfirmDelete] = useState({id: null, open: false, confirm: false});

    const confirmHandleDelete = async (id) => {
      if (!id) { return; }
      setConfirmDelete({id: id, open: true, confirm: false});
    }

    const closeConfirmHandleDelete = () => {
      setConfirmDelete({id: null, open: false, confirm: false});
    }

  const handleUpdate = async (object) => {
      setInitialData(object);
      setTrigger(true);
  };

  const handleAcknowledgments = async (object) => {
    setInitialData(object);
    setAck(true);
    getAnnouncementsAck(object.id);
  };

  const closeAnnouncementsAck = () => {
    setInitialData(null);
    setAck(false);
  }

  const handleDelete = async () => {
    if (confirmDelete.id != null) {
      const removed = await deleteAnnouncement(confirmDelete.id);
      if ( removed ) { 
        closeConfirmHandleDelete();
        getAnnouncements();
        return;
      }
    }
  };

  const openAnnouncements = () => {
    setTrigger(true);
  }
  const closeAnnouncements = () => {
    setTrigger(false);
    setInitialData(null);
    getAnnouncements();
  }
  
  const allowAdminEdit = (staff_id) => {
    if (role.role === ADMIN && role.id === staff_id){ return true; }
    if (role.role === ROOT) { return true; }
    return false;
  }
  
  const getAnnouncements = async () => {
    await fetchAnnouncements(locations);
  }

  const getAnnouncementsAck = async (announcement_id) => {
    await fetchAnnouncementAck(announcement_id);
  }

  useEffect(() => {
    getAnnouncements()
  }, [])

  return (
    <section>
  <Card>
    <CardContent>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, textAlign: { xs: 'left', md: 'left' } }}
      >
        Announcements
      </Typography>
      <Box sx={{ display: "flex", justifyContent: "flex-end", marginBottom: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddRounded />}
          onClick={() => openAnnouncements()}
          disabled={!hasPermission('write', 'announcements')}
        >
          Add announcement
        </Button>
      </Box>
      <TableContainer 
      elevation={0} 
      sx={{ 
        maxHeight: 600,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'auto'
      }}
    >
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ minWidth: 180 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <TitleIcon fontSize="small" />
                <Typography variant="subtitle2" fontWeight="600">Title</Typography>
              </Stack>
            </TableCell>
            <TableCell sx={{ minWidth: 250 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <DescriptionIcon fontSize="small" />
                <Typography variant="subtitle2" fontWeight="600">Body</Typography>
              </Stack>
            </TableCell>
            <TableCell sx={{ width: 160 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <CalendarTodayIcon fontSize="small" />
                <Typography variant="subtitle2" fontWeight="600">Created</Typography>
              </Stack>
            </TableCell>
            <TableCell sx={{ width: 150 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <LocationIcon fontSize="small" />
                <Typography variant="subtitle2" fontWeight="600">Location</Typography>
              </Stack>
            </TableCell>
            <TableCell sx={{ width: 150 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <ProgramIcon fontSize="small" />
                <Typography variant="subtitle2" fontWeight="600">Program</Typography>
              </Stack>
            </TableCell>
            <TableCell sx={{ width: 180 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <PersonIcon fontSize="small" />
                <Typography variant="subtitle2" fontWeight="600">Created By</Typography>
              </Stack>
            </TableCell>
            <TableCell align="center" sx={{ width: 120 }}>
              <Stack direction="row" alignItems="center" spacing={1} justifyContent="center">
                <ActionsIcon fontSize="small" />
                <Typography variant="subtitle2" fontWeight="600">Actions</Typography>
              </Stack>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {announcements && announcements.map((announcement) => {
            const allowed = allowAdminEdit(announcement.staff_id);
            return (
              <TableRow 
              key={announcement.id}
              hover
              sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
            >
              <TableCell>
                <Typography variant="body2" fontWeight="500" noWrap>
                  {announcement.title}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography 
                  variant="body2" 
                  noWrap 
                  sx={{ 
                    maxWidth: 300,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {announcement.body}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                    {DateTime.fromJSDate(new Date(announcement.created_at)).toLocaleString()}
                  </Typography>
              </TableCell>
              <TableCell>
                <Chip 
                  label={announcement.location_name} 
                  size="small" 
                  variant="outlined"
                  color="info"
                />
              </TableCell>
              <TableCell>
                {announcement.program_name && (
                  <Chip 
                    label={announcement.program_name} 
                    size="small" 
                    color="error"
                    variant="outlined"
                  />
                )}
              </TableCell>
              <TableCell>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                    {announcement.staff_name?.charAt(0) || '?'}
                  </Avatar>
                  <Typography variant="body2" noWrap>
                    {announcement.staff_name}
                  </Typography>
                </Stack>
              </TableCell>
              
              <TableCell align="center">
                <Stack direction="row" spacing={0.5} justifyContent="center">
                  <Tooltip title="Edit">
                    <IconButton
                      color="inherit"
                      size="small"
                      onClick={() => handleUpdate(announcement)}
                      disabled={!(hasPermission('write', 'announcements') && allowed)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Acknowledgments">
                    <IconButton
                      color="inherit"
                      size="small"
                      onClick={() => handleAcknowledgments(announcement)}
                      disabled={!(hasPermission('view', 'announcements') && allowed)}
                    >
                      <ThumbUpOffAlt fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => confirmHandleDelete(announcement.id)}
                      disabled={!hasPermission('delete', 'announcements')}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </TableCell>
            </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
    </CardContent>
  </Card>
  <AddAnnouncement
    open={trigger}
    initialData={initialData}
    onClose={closeAnnouncements}
  />

  <DeleteConfirmationDialog open={confirmDelete.open} onClose={closeConfirmHandleDelete} onConfirm={handleDelete} />
  <AnnouncementsAck open={ack} onClose={closeAnnouncementsAck} data={announcementAck} />
</section>

  );
};

export default AnnouncementsList;